import axios from 'axios';
import urlJoin from 'url-join';

import {
    GROUND_GARDEN_PRIVATE,
    PROFILE_BUYER_BUY,
    PROFILE_STARTED_SELLING,
    PROPERTY_TYPES,
} from 'constants/Global';
import { addTokenToHeaders } from 'helpers/Estima';
import { isApartment, isBarometreStepNumber, isHouse } from 'helpers/FormInformations';
import { removePrivateAndNullValues } from 'helpers/ObjectUtils';
import { getSearchParamsAsObject } from 'helpers/Referrer';
import { getFields } from 'helpers/Steps';
import { STEPS } from 'constants/Steps';

const urlParams = getSearchParamsAsObject();

/* eslint-disable no-param-reassign */

/**
 * Compute itemType and itemSubtype from given prropertyTypeId
 * @param {Object} values Current form
 */
export function itemTypeComputation(values) {
    const propertyType = PROPERTY_TYPES.filter((type) => type.id === values._property_type);
    if (propertyType.length > 0) {
        values.item_type = propertyType[0].type;
        values.item_subtype = propertyType[0].subType;
    }

    if (!isHouse(values)) {
        delete values.site_area;
        delete values.site_buildable_area;
    }

    if (!isApartment(values)) {
        delete values.floor_count;
    }
}

/**
 * Compute bathroom boolean from bathroom count.
 * @param {Object} values Current form
 */
function bathroomComputation(values) {
    values.bathroom = values.bathroom_count > 0;
}

/**
 * Compute bathroom boolean from bathroom count.
 * @param {Object} values Current form
 */
function groundGardenComputation(values) {
    if (values.floor !== 0) {
        delete values.ground_garden;
        delete values.ground_garden_area;
    } else if (values.ground_garden !== GROUND_GARDEN_PRIVATE) {
        delete values.ground_garden_area;
    }
}

/**
 * Optional features remove if checkbox not true.
 * @param {Object} values Current form
 */
function optionalFeaturesComputation(values) {
    const fields = getFields(STEPS.filter((step) => step.id === 'features')[0], values);
    fields
        .filter((field) => !/_(count|area)$/.test(field))
        .forEach((prop) => {
            if (!values[prop]) {
                delete values[`${prop}_count`];
                delete values[`${prop}_area`];
            }
        });
}

/**
 * Compute energy consumption to return a string value
 * @param {Object} values Current form
 */
function energyConsumptionComputation(values) {
    if (values.energy_consumption !== undefined) {
        values.energy_consumption = String(values.energy_consumption);
    }
}

/**
 * Compute profile to return only wanted values.
 * @param {Object} values Current form
 */
function profileComputation(values) {
    if (!values.profile_owner || values.user_is_pro) {
        if (values.profile_buyer !== PROFILE_BUYER_BUY) {
            delete values.profile_residence_type;
        }
        delete values.profile_seller;
        delete values.profile_sale_method;
        delete values.profile_optin_realtors;
        delete values.user_phone;
    }

    if (values.profile_owner || values.user_is_pro) {
        delete values.profile_buyer;
        delete values.profile_incentive;
        delete values.profile_needs_loan_status;
        delete values.profile_needs_to_sell;
        delete values.profile_needs_loan;
    }

    if (!values.profile_owner && values.profile_buyer !== PROFILE_BUYER_BUY) {
        delete values.profile_incentive;
        delete values.profile_needs_loan_status;
        delete values.profile_needs_to_sell;
        delete values.profile_needs_loan;
    }

    if (!values.user_is_pro) {
        delete values.user_profile;
        delete values.user_profile_realtor_positions;

        if (values.profile_seller !== PROFILE_STARTED_SELLING) {
            delete values.profile_sale_method;
        }

        if (!values.profile_optin_realtors) {
            delete values.user_phone;
        }
    } else {
        delete values.profile_owner;
    }
}

/* eslint-enable no-param-reassign */

/**
 * Transform form data to the API scheme.
 * @param  {Object} values Form data
 * @returns Values in the API scheme
 */
export function prepareValuesToPost(values) {
    const submittedValues = { ...values };

    itemTypeComputation(submittedValues);
    bathroomComputation(submittedValues);
    groundGardenComputation(submittedValues);
    optionalFeaturesComputation(submittedValues);
    energyConsumptionComputation(submittedValues);
    profileComputation(submittedValues);

    submittedValues.barometre = isBarometreStepNumber(values._stepNumber);

    if (urlParams.partner_id) {
        submittedValues.partner_id = urlParams.partner_id;
    }

    removePrivateAndNullValues(submittedValues);

    return submittedValues;
}

/**
 * This function is used to autofill the username (which is an email) form input
 * when the user click on the switchToSignin button.
 */
export async function getEmailFromToken() {
    const { token } = getSearchParamsAsObject();

    if (token) {
        try {
            const { data: email } = await axios.get(`${MA.Config.WWW_URL}/contact/token/email`, {
                headers: {
                    'X-MA-TOKEN': token || null,
                },
            });

            return email;
        } catch (e) {
            throw new Error(e);
        }
    }

    return null;
}

/**
 * Estima Form API Service.
 */
export default class EstimaFormService {
    /**
     * EstimaForm validation.
     * @return {Promise} Request
     */
    static validate(values) {
        return axios.post(urlJoin(MA.Config.API_URL, 'validate'), prepareValuesToPost(values), {
            headers: addTokenToHeaders(),
        });
    }

    /**
     * EstimaForm submit.
     * @return {Promise} Request
     */
    static submit(values) {
        return axios.post(urlJoin(MA.Config.API_URL, 'create'), prepareValuesToPost(values), {
            headers: addTokenToHeaders(),
        });
    }

    /**
     * Check if given lat and lng are in Media zone
     * @param  {Number}  lat Latitude
     * @param  {Number}  lng Longitude
     * @return {Promise}     Request
     */
    static isZoneMedia(lat, lng) {
        return axios.get(urlJoin(MA.Config.API_URL, 'is_zone_transac'), { params: { lat, lng } });
    }
}
